// packages import
import { Box, Switch, Typography } from '@mui/material'
import { FC } from 'react'
// component import
import { Alert } from '../../common/Alert'
// others
import { Maybe, useUpdateUserMfaSettingsMutation, User, UserStatus } from '../../../../generated'
import {
  MFA_AUTH_ADMIN_NOTE,
  MFA_AUTH_LABEL,
  MFA_EMAIL_AVAILABLE,
  MFA_EMAIL_DESCRIPTION,
  MFA_EMAIL_LABEL,
  MFA_EMAIL_UNAVAILABLE,
  MFA_ERROR_CODES,
  MFA_USER_ENABLED_SUCCESS,
  SOMETHING_WENT_WRONG,
  TWO_FACTOR_AUTHENTICATION_LABEL
} from '../../../constants'
import { DispatchAndState } from '../../../interfaceTypes'
import { UserAction, UserActionType, UserState } from '../userReducer'

/**
 * Lets a super admin take a second factor off the account being edited — what a user who
 * has lost their authenticator needs — and switch the emailed code on or off for them.
 *
 * The authenticator is one-way on purpose: its secret only exists once the user has
 * scanned the QR code on their own profile, so an administrator can disable it but never
 * arm it. Its switch goes dead the moment it is off, and the API refuses the request
 * regardless of what the screen sends.
 */
export const UpdateMfaSettings: FC<DispatchAndState<UserState, UserAction>> = ({
  state,
  dispatch: userDispatch,
  refetch
}): JSX.Element => {
  const { currentUser, usersData } = state || {}
  const { id, status, mfaEmailEnabled, mfaAuthEnabled } = currentUser || {}
  const isActive = status === UserStatus.Active

  const [updateUserMfaSettings, { loading }] = useUpdateUserMfaSettingsMutation({
    onError({ message }) {
      // The global error link already explains a rejected change, and the switches are
      // driven by stored state rather than optimistic UI, so there is nothing to undo.
      if (!MFA_ERROR_CODES.includes(message)) {
        Alert.error(SOMETHING_WENT_WRONG)
      }
    },

    onCompleted(data) {
      if (!data) return

      const { updateUserMfaSettings } = data
      const { response, user } = updateUserMfaSettings || {}
      const { status: responseStatus } = response || {}

      if (responseStatus !== 200 || !user) return

      // The mutation returns only the two flags, so patch the user in place rather than
      // replacing it — everything else on the row is still needed by the modal.
      const { mfaEmailEnabled: nextEmailEnabled, mfaAuthEnabled: nextAuthEnabled } = user
      const patch = { mfaEmailEnabled: nextEmailEnabled, mfaAuthEnabled: nextAuthEnabled }

      userDispatch({
        type: UserActionType.SET_CURRENT_USER,
        currentUser: { ...currentUser, ...patch } as Maybe<User>
      })

      if (usersData) {
        userDispatch({
          type: UserActionType.SET_USERS_DATA,
          usersData: usersData.map(toUpdateUser =>
            toUpdateUser?.id === id ? ({ ...toUpdateUser, ...patch } as Maybe<User>) : toUpdateUser
          )
        })
      }

      Alert.success(MFA_USER_ENABLED_SUCCESS)
      refetch?.()
    }
  })

  const handleToggle = async (mfaSettings: { mfaEmailEnabled?: boolean; mfaAuthEnabled?: boolean }) => {
    await updateUserMfaSettings({ variables: { mfaSettingsInput: { userId: id || '', ...mfaSettings } } })
  }

  return (
    <Box pb={2}>
      <Typography variant="h5" fontSize="14px">
        {TWO_FACTOR_AUTHENTICATION_LABEL}
      </Typography>

      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} pt={1}>
        <Box>
          <Typography variant="subtitle2">{MFA_EMAIL_LABEL}</Typography>

          <Typography variant="subtitle1" component="p">
            {MFA_EMAIL_DESCRIPTION} {!MFA_EMAIL_AVAILABLE && MFA_EMAIL_UNAVAILABLE}
          </Typography>
        </Box>

        <Switch
          name="mfaEmailEnabled"
          checked={!!mfaEmailEnabled}
          disabled={loading || !isActive || !MFA_EMAIL_AVAILABLE}
          onChange={event => handleToggle({ mfaEmailEnabled: event.target.checked })}
          inputProps={{ 'aria-label': MFA_EMAIL_LABEL }}
        />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} pt={1}>
        <Box>
          <Typography variant="subtitle2">{MFA_AUTH_LABEL}</Typography>

          <Typography variant="subtitle1" component="p">
            {MFA_AUTH_ADMIN_NOTE}
          </Typography>
        </Box>

        <Switch
          name="mfaAuthEnabled"
          checked={!!mfaAuthEnabled}
          disabled={loading || !isActive || !mfaAuthEnabled}
          onChange={() => handleToggle({ mfaAuthEnabled: false })}
          inputProps={{ 'aria-label': MFA_AUTH_LABEL }}
        />
      </Box>
    </Box>
  )
}
